import {
  faArrowLeft,
  faCamera,
  faCircleCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  createMatchingRequest,
  editMatch,
  editMatchAll,
  getMatchingCategory,
  getMatchingSubCategory,
  getMatchingSubDetailCategory,
} from "firebase_/firebase_function/matching";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactModal from "react-modal";
import { emptyCheck } from "util/function/function";
import CircleCheckIcon from "assets/dialog_assets/circleCheck.svg";
import { useUser } from "util/context/user_context";
import OnlyLoadingScreen from "components/loading_screen";
import { useParams } from "react-router-dom";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";

/// ✅ MAX사이즈
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const RequestDialog = ({ open, onSubmit, setIsDialogOpen, isEdit, data }) => {
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState([]);
  const [selectedSubDetailCategory, setSelectedSubDetailCategory] = useState(
    []
  );
  const [etc, setEtc] = useState("");
  const [num, setNum] = useState(null);
  const [status, setStatus] = useState(0);
  const imageInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userData } = useUser();
  const [onlyConfirmModal, setOnlyConfirmModal] = useState(false);
  // 확인 팝업이 닫힐 때 페이지 전체를 리로드할지 여부.
  // 검증 에러(글자수/용량 초과 등)에선 false, 요청 수정 성공/실패 후에는 true.
  const [shouldReloadOnConfirm, setShouldReloadOnConfirm] = useState(false);
  const { docId } = useParams();
  const [modalMessage, setModalMessage] = useState("");

  // 카테고리 선택 시 상태 업데이트
  useEffect(() => {
    const fetchData = async () => {
      let data = [];

      // 카테고리 상태에 따라 다른 데이터를 가져옵니다.
      // status 0: 대분류, 1: 중분류, 2: 소분류, 3: 기타, 4: 완료

      switch (status) {
        case 0:
          data = await getMatchingCategory();
          break;
        case 1:
          data = await getMatchingSubCategory(selectedCategory[1]);
          break;
        case 2:
          data = await getMatchingSubDetailCategory(selectedSubCategory[1]);

          break;

        default:
          data = [];
          break;
      }
      if (emptyCheck(data)) setStatus(3);
      // 카테고리 목록을 업데이트합니다.
      setCategoryList(data);
    };

    fetchData();
  }, [status]);

  const handleFileChange = (e) => {
    if (images.length >= 5) {
      setOnlyConfirmModal(true);
      setModalMessage("5장이상 업로드 하실 수 없습니다.");
      return;
    }

    const files = Array.from(e.target.files);
    const imageUrls = files.map((file) => URL.createObjectURL(file));

    /// ✅ file이 아니면 return
    if (!(files[0] instanceof File)) {
      return;
    }

    if (files[0].size > MAX_FILE_SIZE) {
      setOnlyConfirmModal(true);
      setModalMessage("사진 용량이 10MB를 초과했습니다.");
      return;
    }

    const body = {
      // map으로 변환
      file: files,
      url: imageUrls,
    };

    setImages((prev) => [...prev, body]);
  };

  // 요청하기 버튼 클릭 시 호출되는 함수
  const handleRequest = async () => {
    const imageFiles = images.map((imageData) =>
      typeof imageData === "string" ? imageData : imageData.file[0]
    );

    const categoryList = [
      selectedCategory ? selectedCategory[0] : 0,
      selectedSubCategory ? selectedSubCategory[0] : 0,
      selectedSubDetailCategory ? selectedSubDetailCategory[0] : 0,
    ];

    const categoryListName = [
      selectedCategory ? selectedCategory[1] : "",
      selectedSubCategory ? selectedSubCategory[2] : "",
      selectedSubDetailCategory ? selectedSubDetailCategory[2] : "",
    ];

    setLoading(true);

    const response = await createMatchingRequest(
      userData,
      categoryList,
      categoryListName,
      etc,
      imageFiles
    );

    if (response) {
      setLoading(false);
      initialize();
      setIsDialogOpen(false);
      setConfirmModal(true);
    }
  };

  // 모달 닫기 함수
  const closeModal = () => {
    initialize();
    setIsDialogOpen(false);
    setConfirmModal(false);
    sessionStorage.removeItem(`page_/matching`);
    sessionStorage.removeItem(`pageIndex_/matching`);
    window.location.reload();
  };

  // 초기화 함수
  // 모든 상태를 초기화합니다.
  const initialize = () => {
    if (isEdit) {
      setSelectedCategory([
        data?.categoryList[0],
        data?.selectList,
        data?.categoryListName[0],
      ]);
      setSelectedSubCategory([
        data?.categoryList[1],
        data?.selectList === "" ? "기타" : data?.selectList,
        data?.categoryListName[1] === "" ? "기타" : data?.categoryListName[1],
      ]);
      setSelectedSubDetailCategory([
        data?.categoryList[2],
        data?.selectList === "" ? "기타" : data?.selectList,
        data?.categoryListName[2] === "" ? "기타" : data?.categoryListName[2],
      ]);
      setStatus(0);
      setNum(data?.categoryList[0]);
      loadImage();
    } else {
      setSelectedCategory([]);
      setSelectedSubCategory([]);
      setSelectedSubDetailCategory([]);
      setEtc("");
      setNum(null);
      setStatus(0);
      setImages([]);
    }
  };

  /// ✅ 이미지 제거
  const handleDelete = (indexToDelete) => {
    setImages((prev) => prev.filter((_, idx) => idx !== indexToDelete));
  };

  /// ✅ 수정 할 때 기존 이미지 로드
  const loadImage = () => {
    const imageUrls = data?.imageUrls || [];
    setImages(imageUrls);
  };

  /// ✅ 매치 수정
  const handleEditPost = async () => {
    /// file만 추출
    const imageFiles = images.map((imageData) =>
      typeof imageData === "string" ? imageData : imageData.file[0]
    ); // file : firebaseUrl

    const categoryList = [
      selectedCategory[0] || 0,
      selectedSubCategory[0] || 0,
      selectedSubDetailCategory[0] || 0,
    ];

    const categoryListName = [
      selectedCategory[2],
      selectedSubCategory[2] === "기타" ? "" : selectedSubCategory[2] || "",
      selectedSubDetailCategory[2] === "기타"
        ? ""
        : selectedSubDetailCategory[2] || "",
    ];

    setLoading(true);

    const result = await editMatchAll(
      docId,
      categoryList,
      categoryListName,
      etc,
      imageFiles
    );

    setLoading(false);
    setIsDialogOpen(false);
    // 수정 후에는 목록 갱신을 위해 리로드 필요
    setShouldReloadOnConfirm(true);
    if (result) {
      setOnlyConfirmModal(true);
      setModalMessage("요청 수정에 성공했습니다.");
    } else {
      setOnlyConfirmModal(true);
      setModalMessage("요청 수정에 실패했습니다.");
    }
  };

  // useMemo를 사용하여 open과 confirmModal이 변경될 때마다 initialize 함수를 호출합니다.
  useMemo(() => {
    initialize();
  }, [open, confirmModal, data]);

  useEffect(() => {
    /// ✅ 수정데이터 받으면 etc상태 세팅
    if (data) {
      setEtc(data.etc);
    }
  }, [data]);

  // 이전 단계로 가기 버튼 클릭 시 호출되는 함수
  const handleRequestPrevious = () => {
    switch (status) {
      case 1:
        setStatus(0);
        setNum(selectedCategory[0]);
        setSelectedSubCategory(["", "", ""]);
        break;

      case 2:
        setStatus(1);
        setNum(selectedSubCategory[0]);
        setSelectedSubDetailCategory(["", "", ""]);
        break;
      case 3:
        setStatus(0);
        setNum(selectedCategory[0]);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ReactModal
        isOpen={open}
        shouldCloseOnOverlayClick={false}
        style={{
          overlay: {
            zIndex: 100,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
          content: {
            fontFamily: "InterVariable",
            zIndex: "150",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
            backgroundColor: "white",
            overflow: "auto",
            width: "800px",
            height: "fit-content",
            padding: "0px",
            userSelect: "none",
          },
        }}
      >
        <div
          style={{
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "24px",
              alignItems: "center",
              padding: "18px",
            }}
          >
            <button
              disabled={status === 0}
              style={{ cursor: status !== 0 ? "pointer" : "default" }}
              onClick={handleRequestPrevious}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                size="lg"
                color={status !== 0 ? "black" : "grey"}
              />
            </button>
            <div className="f20_1F2937_W600">
              {isEdit ? "수리 요청 수정하기" : "수리 요청하기"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsDialogOpen(false);
            }}
            style={{ padding: "0px 18px" }}
          >
            <FontAwesomeIcon icon={faXmark} size="xl" color="#6B7280" />
          </button>
        </div>
        <div
          style={{
            padding: "18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#2E4A24",
                borderRadius: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="f16_FFFFFF_W400">1</div>
            </div>
            <div className="f16_2e4a24_W500">분류</div>
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: "212px",
              height: "4px",
              background: "#E5E7EB",
            }}
          >
            <div
              className="transition-all duration-500"
              style={{
                width:
                  status >= 1
                    ? status >= 2
                      ? status >= 3
                        ? "100%"
                        : "66%"
                      : "33%"
                    : "0%",
                height: "100%",
                background: "#2E4A24",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: status < 3 ? "#E5E7EB" : "#2E4A24",
                borderRadius: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="f16_FFFFFF_W400">2</div>
            </div>
            <div className="f16_2e4a24_W500">기타</div>
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: "212px",
              height: "4px",
              background: "#E5E7EB",
            }}
          >
            <div
              className="transition-all duration-500"
              style={{
                width: status > 3 ? "100%" : "0%",
                height: "100%",
                background: "#2E4A24",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: status < 4 ? "#E5E7EB" : "#2E4A24",
                borderRadius: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="f16_FFFFFF_W400">3</div>
            </div>
            <div className="f16_2e4a24_W500">완료</div>
          </div>
        </div>

        <div
          style={{
            padding: "18px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minHeight: "156px",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <div className="f18_1F2937_W500">
            {status > 2 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "end",
                  justifyContent: "space-between",
                }}
              >
                <div className="f18_1F2937_W500">기타내용</div>
                <div className="f14_6B7280_W400">{`(${etc.length}/300)`}</div>
              </div>
            ) : (
              <>
                {status < 1
                  ? "요청 상세 (대분류)"
                  : status < 2
                  ? "요청 상세 (중분류)"
                  : "요청 상세 (소분류)"}
              </>
            )}
          </div>
          {status < 3 ? (
            <>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                }}
              >
                {categoryList.map((category, index) => (
                  <button
                    className={`hover:bg-black/5 transition-all active:bg-black/10 f16_1F2937_W500`}
                    key={index}
                    style={{
                      height: "84px",
                      width: "100%",
                      maxWidth: "240px",
                      border: "2px solid #E5E7EB",
                      borderRadius: "12px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: num === index ? "#98B88A" : "",
                    }}
                    onClick={() => {
                      if (status < 1) {
                        setSelectedCategory([
                          index,
                          category.tag,
                          category.name,
                        ]);
                        setSelectedSubCategory(["", "", ""]);
                        setSelectedSubDetailCategory(["", "", ""]);
                      } else if (status < 2) {
                        setSelectedSubCategory([
                          index,
                          category.tag,
                          category.name,
                        ]);
                        setSelectedSubDetailCategory(["", "", ""]);
                      } else if (status < 3) {
                        setSelectedSubDetailCategory([
                          index,
                          category.tag,
                          category.name,
                        ]);
                      }
                      setNum(index);
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        padding: "16px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {status < 1 && (
                        <img
                          src={`/${category.icon}`}
                          alt={category.name}
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "12px",
                            objectFit: "cover",
                          }}
                          draggable="false"
                        />
                      )}
                      <div className="f16_1F2937_W500">{category.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <textarea
                  style={{
                    height: "128px",
                    width: "100%",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "16px",
                    resize: "none",
                  }}
                  autoFocus={true}
                  placeholder="문제에 대해 설명해주세요 (예: 언제부터 시작되었는지, 어떤 문제가 있는지, 특이한 소리나 현상이 있는지 등)"
                  defaultValue={data?.etc}
                  onChange={(e) => {
                    if (e.target.value.length > 300) {
                      setOnlyConfirmModal(true);
                      setModalMessage("내용은 300글자 이내로 작성해주세요.");
                      return;
                    }
                    setEtc(e.target.value);
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div className="f16_1F2937_W500">사진 첨부</div>
                    <div>(최대 5장)</div>
                  </div>
                  <div
                    style={{ display: "flex", gap: "8px", overflowX: "auto" }}
                  >
                    <button
                      style={{
                        minWidth: "140px",
                        height: "140px",
                        borderRadius: "12px",
                        border: "1px dashed #E5E7EB",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={() => {
                        imageInputRef.current.click();
                      }}
                    >
                      <FontAwesomeIcon icon={faCamera} />
                      <div>추가</div>
                    </button>
                    {/* 이미지 미리보기 */}
                    {images.map((image, index) => (
                      <div key={index} style={{ position: "relative" }}>
                        <img
                          src={image.url ?? image} // 로컬파일 ?? 파베이미지
                          alt="preview"
                          style={{
                            width: "140px",
                            height: "140px",
                            minWidth: "140px",
                            borderRadius: "12px",
                            objectFit: "cover",
                          }}
                        />

                        {/* X 버튼 */}
                        <button
                          type="button"
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            borderRadius: "100%",
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            zIndex: 1,
                          }}
                          onClick={() => handleDelete(index)}
                        >
                          <FontAwesomeIcon
                            icon={faXmark}
                            size="xs"
                            color="white"
                          />
                        </button>
                      </div>
                    ))}
                    <input
                      ref={imageInputRef}
                      style={{ display: "none" }}
                      s
                      // multiple
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex w-full p-4 gap-4">
          {categoryList?.length > 0 ? (
            <>
              <button
                style={{
                  background: "#2E4A24",
                  height: "48px",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "500",
                  width: "100%",
                }}
                onClick={() => {
                  if (!emptyCheck(num)) {
                    if (num === categoryList.length - 1) {
                      setStatus(3);
                      return;
                    }
                    setStatus((prev) => prev + 1);
                    switch (status) {
                      case 0:
                        selectedSubCategory
                          ? setNum(selectedSubCategory[0])
                          : setNum(null);
                        break;
                      case 1:
                        selectedSubDetailCategory
                          ? setNum(selectedSubDetailCategory[0])
                          : setNum(null);
                        break;
                    }
                  } else {
                    // 분류 미선택 상태 — 무반응 대신 안내
                    setModalMessage("분류를 먼저 선택해주세요.");
                    setOnlyConfirmModal(true);
                  }
                }}
              >
                다음 단계로
              </button>
            </>
          ) : (
            <>
              {isEdit ? (
                <button
                  style={{
                    background: "#2E4A24",
                    height: "48px",
                    borderRadius: "12px",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "500",
                    width: "100%",
                  }}
                  onClick={() => {
                    handleEditPost();
                    setStatus(4);
                  }}
                >
                  수리 수정하기
                </button>
              ) : (
                <button
                  style={{
                    background: "#2E4A24",
                    height: "48px",
                    borderRadius: "12px",
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "500",
                    width: "100%",
                  }}
                  onClick={() => {
                    handleRequest();
                    setStatus(4);
                  }}
                >
                  수리 요청하기
                </button>
              )}
            </>
          )}
        </div>
      </ReactModal>
      <ReactModal
        isOpen={confirmModal}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={true}
        style={{
          overlay: {
            zIndex: 100,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
          content: {
            fontFamily: "InterVariable",
            zIndex: "150",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
            backgroundColor: "white",
            alignItems: "center",
            overflow: "auto",
            width: "362px",
            height: "fit-content",
            padding: "0px",
            userSelect: "none",
            padding: "24px",
            boxShadow: "0px 25px 50px rgba(0, 0, 0, 0.25)",
            gap: "26px",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "18px",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={CircleCheckIcon}
            alt="success"
            style={{
              width: "36px",
              height: "36px",
            }}
          />
          <div
            style={{
              display: "flex",

              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div className="f20_111827_W600">요청이 완료되었습니다</div>
            <div className="f14_4B5563_W400">성공적으로 처리되었습니다.</div>
          </div>
        </div>
        <button
          type="button"
          className="f16_FFFFFF_W400"
          style={{
            padding: "12px",
            backgroundColor: "#2E4A24",
            borderRadius: "8px",
            width: "194px",
          }}
          onClick={closeModal}
        >
          확인
        </button>
      </ReactModal>
      <OnlyLoadingScreen isOpen={loading} />
      <OnlyConfirmDialog
        open={onlyConfirmModal}
        onClose={() => {
          setOnlyConfirmModal(false);
          if (shouldReloadOnConfirm) {
            setShouldReloadOnConfirm(false);
            window.location.reload();
          }
        }}
        message={modalMessage}
      />
    </>
  );
};

export default RequestDialog;
